import React, { useMemo } from 'react';

import styled from 'styled-components';

import useConfigurations from '@hooks/Configurations/UseConfigurations';

import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import { getDineroAmount, getDisplayedStatementAmount } from '@components/StatementRowsCommon/helpers';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// Gruppi di totale mostrati nella banda, nell'ordine di visualizzazione.
const TOTAL_GROUPS = [
    { key: 'fees', label: 'Fees' },
    { key: 'agencyFees', label: 'Agency Fees' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'clientCredits', label: 'Client Credits' },
];

// Assegna lo statement a un gruppo in base alla Family della transaction.
// Per i JobFee la categoria dipende dal flag isCommission del singolo statement.
const getStatementCategory = (family, statement) => {
    switch (family) {
        case 'JobFee':
            return statement.isCommission ? 'agencyFees' : 'fees';
        case 'Product':
            return 'fees';
        case 'AgFee':
            return 'agencyFees';
        case 'JobExpense':
        case 'JobRefund':
            return 'expenses';
        case 'JobCredit':
        case 'JobAdvance':
            return 'clientCredits';
        default:
            return null;
    }
};

// Ordina le valute mettendo la defaultCurrency per prima, poi alfabetico.
const toSortedCurrencyArray = (totalsByCurrency, defaultCurrency) => {
    return Object.entries(totalsByCurrency || {})
        .sort(([currencyA], [currencyB]) => {
            if (currencyA === defaultCurrency) {
                return -1;
            }
            if (currencyB === defaultCurrency) {
                return 1;
            }
            return currencyA.localeCompare(currencyB);
        })
        .map(([Currency, Dinero]) => ({ Currency, Dinero }));
};

// Somma gli Amount dei ClientStatements raggruppati per categoria, valuta e
// tipo (normale, option, cancelled). Gli importi con ExchangeRate valido
// vengono convertiti in defaultCurrency tramite getDisplayedStatementAmount.
export const computeTransactionsTotals = (transactions, defaultCurrency) => {
    const buckets = {};

    const addAmount = (category, type, currency, amount) => {
        if (!buckets[category]) {
            buckets[category] = { normal: {}, option: {}, cancelled: {} };
        }
        if (!buckets[category][type][currency]) {
            buckets[category][type][currency] = getDineroAmount(0, currency, defaultCurrency);
        }
        buckets[category][type][currency] = buckets[category][type][currency].add(
            getDineroAmount(amount, currency, defaultCurrency)
        );
    };

    if (Array.isArray(transactions)) {
        for (const model of transactions) {
            for (const date of model.Dates || []) {
                for (const transaction of date.Transactions || []) {
                    for (const statement of transaction.ClientStatements || []) {
                        const category = getStatementCategory(transaction.Family, statement);
                        if (!category) {
                            continue;
                        }
                        // Gli statement annidati possono non avere la valuta: si usa quella della transazione
                        const { amount, currency } = getDisplayedStatementAmount(
                            { ...statement, Currency: statement.Currency || transaction.Currency },
                            defaultCurrency
                        );
                        const resolvedCurrency = currency || defaultCurrency;
                        // Le giornate annullate sono totalizzate a parte, poi le option.
                        let type = 'normal';
                        if (transaction.WorkDateCancelled) {
                            type = 'cancelled';
                        } else if (statement.Status === 'Option') {
                            type = 'option';
                        }
                        addAmount(category, type, resolvedCurrency, amount);
                    }
                }
            }
        }
    }

    // Esclude i gruppi senza importi: le colonne vuote non vengono mostrate.
    return TOTAL_GROUPS.map((group) => ({
        key: group.key,
        label: group.label,
        normal: toSortedCurrencyArray(buckets[group.key]?.normal, defaultCurrency),
        option: toSortedCurrencyArray(buckets[group.key]?.option, defaultCurrency),
        cancelled: toSortedCurrencyArray(buckets[group.key]?.cancelled, defaultCurrency),
    })).filter((group) => group.normal.length || group.option.length || group.cancelled.length);
};

const TotalsBand = styled.div`
    display: flex;
    align-items: space-between;
    gap: 1.5rem;
    width: 100%;
    padding: 0.75rem 1rem;
    /* margin-top: 0.5rem; */
    background: white;
    border-bottom: 2px solid ${(props) => props.theme.PrimaryColor};
    background-color: ${(props) => props.theme.HoverablesBackground};

    .totals-summary {
        flex: 1 1 0;
        min-width: 120px;

        .totals-icon {
            color: ${(props) => props.theme.TextColor};
            font-size: 1.25rem;
            line-height: 1;
            opacity: 0.5;
            margin-bottom: 0.25rem;
            flex: 1 1 0;
        }

        .totals-grand {
            font-size: 0.95rem;
            line-height: 1.3;
            font-weight: bold;
        }
    }

    .totals-columns {
        flex: 1 1 0;
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: ${(props) => (props.$eventDrawer ? '1rem' : '4rem')};

        //under 1300px 2 columns per row
        @media (max-width: 1300px) {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 2rem;
            grid-template-rows: repeat(2, 1fr);
        }

        /*
        @media (max-width: 1000px) {
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: 2rem;
            grid-template-rows: repeat(2, 1fr);
        } */
    }

    .totals-column {
        flex: 1 1 0;
        min-width: 120px;
        text-align: right;

        .totals-label {
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
            font-size: ${(props) => props.theme.FontSizeSm};
            margin-bottom: 0.25rem;
        }

        .totals-amounts {
            font-size: 0.95rem;
            line-height: 1.3;
        }

        .totals-muted {
            margin-top: 0.25rem;
            color: ${(props) => props.theme.TextColorMuted};
            font-size: ${(props) => (props.$eventDrawer ? props.theme.FontSizeXSm : props.theme.FontSizeSm)};
            .totals-muted-line {
                display: flex;
                align-items: baseline;
                justify-content: flex-end;
                gap: 0.5rem;

                .totals-muted-label {
                    text-transform: uppercase;
                }
            }
        }
    }
`;

const ProjectTransactionsTotalsBand = ({ transactions, eventDrawer }) => {
    const configurations = useConfigurations();
    const defaultCurrency = configurations.defaultCurrency;

    const totals = useMemo(
        () => computeTransactionsTotals(transactions, defaultCurrency),
        [transactions, defaultCurrency]
    );

    const hasAmounts = totals.some((group) => group.normal.length || group.option.length || group.cancelled.length);

    // Totale complessivo per valuta: somma delle sole righe normali di tutte
    // le colonne (esclude option e cancelled).
    const grandTotal = useMemo(() => {
        const totalsByCurrency = {};
        for (const group of totals) {
            for (const item of group.normal) {
                totalsByCurrency[item.Currency] = totalsByCurrency[item.Currency]
                    ? totalsByCurrency[item.Currency].add(item.Dinero)
                    : item.Dinero;
            }
        }
        return toSortedCurrencyArray(totalsByCurrency, defaultCurrency);
    }, [totals, defaultCurrency]);

    if (!hasAmounts) {
        return null;
    }

    return (
        <TotalsBand $eventDrawer={eventDrawer}>
            <div className="totals-summary">
                <div className="totals-icon">
                    <NwIcon icon={byPrefixAndName.fass['sigma']} />
                </div>
                <div className="totals-grand">
                    {grandTotal.map((item) => (
                        <Money
                            key={item.Currency}
                            dineroObj={item.Dinero}
                            highlightCurrency
                            boldIfDefaultCurrency
                            style={{ display: 'block' }}
                        />
                    ))}
                </div>
            </div>
            <div className="totals-columns">
                {totals.map((group) => (
                    <div className="totals-column" key={group.key}>
                        <div className="totals-label">{group.label}</div>
                        <div className="totals-amounts">
                            {group.normal.map((item) => (
                                <Money
                                    key={item.Currency}
                                    dineroObj={item.Dinero}
                                    highlightCurrency
                                    boldIfDefaultCurrency
                                    style={{ display: 'block' }}
                                />
                            ))}
                            {(group.option.length > 0 || group.cancelled.length > 0) && (
                                <div className="totals-muted">
                                    {group.option.map((item) => (
                                        <div className="totals-muted-line" key={`option-${item.Currency}`}>
                                            <span className="totals-muted-label">option</span>
                                            <Money dineroObj={item.Dinero} style={{ display: 'block' }} />
                                        </div>
                                    ))}
                                    {group.cancelled.map((item) => (
                                        <div className="totals-muted-line" key={`cancelled-${item.Currency}`}>
                                            <span className="totals-muted-label">cancelled</span>
                                            <Money dineroObj={item.Dinero} style={{ display: 'block' }} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </TotalsBand>
    );
};

export default ProjectTransactionsTotalsBand;
